/**
 * Quiz exercises of a book. They are parsed from the quiz XML a `TGCHECKS` content entry of the book points to
 * (e.g. `checks.xml` of Teacher Guide Plus); the exercise id is the `id` of the `<tgcheck>` marker in the book text.
 */
export type QuizType = 'single_select' | 'multi_select' | 'gap_fill' | 'wordpool' | 'matching' | 'blank_fill';

// A piece of rich text: text with its inline formatting (<b>, <i>, <u>), or a gap to fill (value = gap id).
export interface QuizSegment {
  kind: 'text' | 'gap';
  value: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface QuizOption {
  id: string;
  // Plain text, for places that cannot show formatting (a native <select> option).
  text: string;
  segments: QuizSegment[];
}

// Matching exercises: the numbered statements the options are matched to.
export interface QuizItem {
  n: string;
  text: string;
  segments: QuizSegment[];
}

export interface Quiz {
  id: string;
  type: QuizType;
  // One entry (list of segments) per instruction line.
  instruction: QuizSegment[][];
  prompt: QuizSegment[];
  items: QuizItem[];
  options: QuizOption[];
  // Correct answers per slot. The slot key is '' for select exercises, the gap id for gap/blank
  // exercises and the item number for matching. A blank exercise may accept several alternatives.
  answers: Map<string, string[]>;
}

// The key of the only slot of single_select / multi_select exercises.
export const QUIZ_CHOICE_SLOT = '';

export class QuizParser {

  parse(xml: string): Map<string, Quiz> {
    const quizzes = new Map<string, Quiz>();
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    Array.from(doc.getElementsByTagName('exercise')).forEach(exercise => {
      const quiz = this.parseExercise(exercise);
      if (quiz) quizzes.set(quiz.id, quiz);
    });
    return quizzes;
  }

  private parseExercise(exercise: Element): Quiz | null {
    const id = exercise.getAttribute('id');
    const type = exercise.getAttribute('type') as QuizType | null;
    if (!id || !type) return null;

    const prompt = this.child(exercise, 'prompt');
    const isMatching = type === 'matching';
    const quiz: Quiz = {
      id,
      type,
      instruction: this.instruction(this.child(exercise, 'instruction')),
      prompt: prompt && !isMatching ? this.segments(prompt) : [],
      items: prompt && isMatching ? this.items(prompt) : [],
      options: [],
      answers: new Map<string, string[]>()
    };

    Array.from(this.child(exercise, 'options')?.getElementsByTagName('option') ?? []).forEach(option =>
      quiz.options.push({ id: option.getAttribute('id') ?? '', text: this.text(option), segments: this.segments(option) }));
    // A word pool has no option ids: the word itself is the value.
    Array.from(this.child(exercise, 'wordPool')?.getElementsByTagName('word') ?? []).forEach(word =>
      quiz.options.push({ id: this.text(word), text: this.text(word), segments: this.segments(word) }));

    const firstGap = quiz.prompt.find(segment => segment.kind === 'gap')?.value ?? '';
    Array.from(this.child(exercise, 'answers')?.getElementsByTagName('answer') ?? []).forEach(answer => {
      const key = type === 'matching' ? answer.getAttribute('prompt') ?? ''
        : type === 'blank_fill' ? answer.getAttribute('gap') ?? firstGap
        : type === 'gap_fill' || type === 'wordpool' ? answer.getAttribute('gap') ?? ''
        : QUIZ_CHOICE_SLOT;
      quiz.answers.set(key, [...(quiz.answers.get(key) ?? []), this.text(answer)]);
    });
    return quiz;
  }

  private child(parent: Element, name: string): Element | null {
    return Array.from(parent.children).find(element => element.tagName === name) ?? null;
  }

  private text(element: Element): string {
    return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private instruction(element: Element | null): QuizSegment[][] {
    if (!element) return [];
    const lines = Array.from(element.getElementsByTagName('line')).map(line => this.segments(line));
    return lines.length ? lines : [this.segments(element)];
  }

  private items(prompt: Element): QuizItem[] {
    return Array.from(prompt.getElementsByTagName('item'))
      .map(item => ({ n: item.getAttribute('n') ?? '', text: this.text(item), segments: this.segments(item) }));
  }

  private segments(element: Element): QuizSegment[] {
    const segments: QuizSegment[] = [];
    this.collect(element, {}, segments);
    return segments;
  }

  // Walks the content keeping the formatting of the enclosing <b>/<i>/<u> elements (they may be nested).
  private collect(node: Node, format: { bold?: boolean, italic?: boolean, underline?: boolean }, out: QuizSegment[]): void {
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        const value = (child.textContent ?? '').replace(/\s+/g, ' ');
        if (value) out.push({ kind: 'text', value, ...format });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element;
        switch (element.tagName) {
          case 'gap': out.push({ kind: 'gap', value: element.getAttribute('id') ?? '' }); break;
          case 'b': case 'strong': this.collect(element, { ...format, bold: true }, out); break;
          case 'i': case 'em': this.collect(element, { ...format, italic: true }, out); break;
          case 'u': this.collect(element, { ...format, underline: true }, out); break;
          default: this.collect(element, format, out);
        }
      }
    });
  }
}
